import { Tooltip, TooltipProps } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

interface CustomTooltipProps extends TooltipProps {
	autoTranslate?: "yes" | "no";
	showWhenOverflow?: boolean;
	open?: boolean;
}

const isElementOverflowing = (el: HTMLElement): boolean =>
	el.scrollWidth > el.offsetWidth || el.scrollHeight > el.offsetHeight;

const findTruncatingElement = (root: HTMLElement): HTMLElement => {
	if (isElementOverflowing(root)) {
		return root;
	}
	const descendants = root.querySelectorAll<HTMLElement>("*");
	for (let i = 0; i < descendants.length; i += 1) {
		if (isElementOverflowing(descendants[i])) {
			return descendants[i];
		}
	}
	return root;
};

const isOverflowing = (root: HTMLElement | null): boolean => {
	if (!root) {
		return false;
	}
	return isElementOverflowing(findTruncatingElement(root));
};

export default function CustomTooltip({
	autoTranslate = "yes",
	arrow = false,
	children,
	className = "",
	disableHoverListener,
	enterDelay = 100,
	enterNextDelay = 0,
	placement = "bottom",
	showWhenOverflow = false,
	title,
	open,
}: Readonly<CustomTooltipProps>) {
	const ref = useRef<HTMLElement | null>(null);
	const [overflowing, setOverflowing] = useState(false);

	useEffect(() => {
		if (!showWhenOverflow) {
			return undefined;
		}
		const root = ref.current;
		if (!root) {
			return undefined;
		}
		const check = () => setOverflowing(isOverflowing(root));
		check();
		const observer = new ResizeObserver(check);
		observer.observe(root);
		const truncating = findTruncatingElement(root);
		if (truncating !== root) {
			observer.observe(truncating);
		}
		return () => observer.disconnect();
	}, [showWhenOverflow, title, children]);

	const effectiveTitle = showWhenOverflow && !overflowing ? "" : title;

	return (
		<Tooltip
			ref={ref}
			arrow={arrow}
			classes={{
				tooltip: `font-sm-regular ${className}`,
			}}
			disableHoverListener={disableHoverListener}
			enterDelay={enterDelay}
			enterNextDelay={enterNextDelay}
			{...(open !== undefined ? { open } : {})}
			placement={placement}
			title={effectiveTitle}
			translate={autoTranslate}
		>
			{children}
		</Tooltip>
	);
}
